import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule } from '@angular/forms';
import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import moment from 'moment';

import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PaginatorModule } from 'primeng/paginator';

import { DynamicForm } from '@ui-nuvertos/shared/dynamic-form/dynamic-form';
import { ItemDynamicForm } from '@ui-nuvertos/shared/item-dynamic-form/item-dynamic-form';
import { ClientSettlementService, ClientSettlementMapping } from '../services/client/client-settlement.service';
import { ClientListService } from '../services/client/client-list.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { ClientTypeaheadUtils } from '../utils/client-typeahead.utils';

interface FieldConfig {
    field_name: string;
    display_name: string;
    field_type: string;
    data_type: string;
    is_mandatory: boolean;
    is_visible: boolean;
    is_readonly?: boolean;
    options?: any[];
    autocompleteDisplayField?: string;
    forceSelection?: boolean;
    width?: string;
}

@Component({
    selector: 'app-client-settlement',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        CardModule,
        AutoCompleteModule,
        TableModule,
        TagModule,
        TooltipModule,
        PaginatorModule,
        DynamicForm,
        ItemDynamicForm,
    ],
    templateUrl: './client-settlement.html',
    styleUrls: ['./client-settlement.css'],
})
export class ClientSettlement implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    // Forms
    primaryForm!: FormGroup;
    creditModulesForm!: FormGroup;
    debitModulesForm!: FormGroup;
    autoApplyMappingsForm!: FormGroup;

    // Loading states
    isLoading = false;
    isSubmitting = false;
    loadingCreditModules = false;
    loadingDebitModules = false;

    // Pagination for debit modules (local)
    debitModulesPage = 1;
    lastDebitModulesPage = 1; // Track last rendered page
    debitModulesLimit = 15;
    debitModulesTotalCount = 0;
    allDebitModules: any[] = []; // Store all debit modules for local pagination

    // Pagination for credit modules (local)
    creditModulesPage = 1;
    lastCreditModulesPage = 1; // Track last rendered page
    creditModulesLimit = 15;
    creditModulesTotalCount = 0;
    allCreditModules: any[] = []; // Store all credit modules for local pagination

    // Client data
    selectedClient: any = null;
    clientSuggestions: any[] = [];
    allClients: any[] = [];

    // Subsidiary data
    selectedSubsidiary: any = null;
    subsidiarySuggestions: any[] = [];
    allSubsidiaries: any[] = [];

    // Primary information fields
    primaryFields: FieldConfig[] = [
        {
            field_name: 'client_id',
            display_name: 'Client',
            field_type: 'autocomplete',
            data_type: 'number',
            is_mandatory: true,
            is_visible: true,
            options: [],
            autocompleteDisplayField: 'label',
            forceSelection: true,
        },
        {
            field_name: 'subsidiary_id',
            display_name: 'Subsidiary',
            field_type: 'autocomplete',
            data_type: 'number',
            is_mandatory: true,
            is_visible: true,
            options: [],
            autocompleteDisplayField: 'label',
            forceSelection: true,
        },
        {
            field_name: 'mapping_date',
            display_name: 'Mapping Date',
            field_type: 'date',
            data_type: 'date',
            is_mandatory: true,
            is_visible: true,
        },
    ];

    // Debit modules configuration - unified table (Payments received FROM client)
    creditModulesConfig = {
        credit_modules: {
            title: 'Amounts Receivable from Client',
            fields: [
                { field_name: 'module_name', display_name: 'Type', field_type: 'label', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true, width: '80px' },
                { field_name: 'reference_no', display_name: 'Reference', field_type: 'redirect', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'original_amount', display_name: 'Total Amount', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'unsettled_amount', display_name: 'Due Amount', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
            ],
            data: [] as any[],
        },
    };


    //Credit modules configuration - unified table (Invoices and Credit Notes owed BY client)
    debitModulesConfig = {
        debit_modules: {
            title: 'Payments Received from Client',
            fields: [
                { field_name: 'module_name', display_name: 'Type', field_type: 'label', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true, width: '80px' },
                { field_name: 'reference_no', display_name: 'Reference', field_type: 'redirect', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'amount', display_name: 'Total Amount', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'unsettled_amount', display_name: 'Unmapped Amount', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
            ],
            data: [] as any[],
        },
    };

    // Auto-apply mappings configuration for ItemDynamicForm
    autoApplyMappingsConfig = {
        mappings: {
            title: 'Auto-Apply Mappings',
            fields: [

                { field_name: 'credit_module_name', display_name: 'Type', field_type: 'label', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'credit_transaction_no', display_name: 'Transaction #', field_type: 'redirect', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'credit_unsettled', display_name: 'Unsettled', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'credit_remaining', display_name: 'Remaining', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'debit_module_name', display_name: 'Type', field_type: 'label', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'debit_transaction_no', display_name: 'Transaction #', field_type: 'redirect', data_type: 'string', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'debit_unsettled', display_name: 'Unmapped', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'debit_remaining', display_name: 'Remaining', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
                { field_name: 'mapped_amount', display_name: 'Mapped Amount', field_type: 'label', data_type: 'number', is_visible: true, is_mandatory: false, is_readonly: true },
            ],
            data: [] as any[],
        },
    };

    // Auto-apply mappings
    autoApplyMappings: any[] = [];

    resolveRedirectQueryParams = (
        field: any,
        control: FormGroup | null | undefined
    ): Record<string, any> => {
        const params = field?.redirect_query_params
            ? { ...field.redirect_query_params }
            : {};

        if (control) {
            // For client invoices - get invoiceId from client_invoice_id field
            const clientInvoiceIdControl = control.get('client_invoice_id');
            const clientInvoiceId = clientInvoiceIdControl?.value;
            if (clientInvoiceId !== undefined && clientInvoiceId !== null && clientInvoiceId !== '') {
                params.invoiceId = String(clientInvoiceId);
            }

            // For client payments - get paymentId from client_payment_id field
            const clientPaymentIdControl = control.get('client_payment_id');
            const clientPaymentId = clientPaymentIdControl?.value;
            if (clientPaymentId !== undefined && clientPaymentId !== null && clientPaymentId !== '') {
                params.paymentId = String(clientPaymentId);
            }

            // For credit notes - get id from credit_note_id field
            const creditNoteIdControl = control.get('credit_note_id');
            const creditNoteId = creditNoteIdControl?.value;
            if (creditNoteId !== undefined && creditNoteId !== null && creditNoteId !== '') {
                params.id = String(creditNoteId);
            }

            // For auto-apply mappings table - handle credit/debit module fields
            if (field.field_name === 'credit_transaction_no') {
                const moduleType = control.get('credit_module_type')?.value;
                const moduleId = control.get('credit_module_id')?.value;
                if (moduleId) {
                    if (moduleType === 'client_invoice') {
                        params.invoiceId = String(moduleId);
                    } else if (moduleType === 'client_credit_note') {
                        params.id = String(moduleId);
                    }
                    // JV credit lines use path-based routing, no query params needed
                }
            } else if (field.field_name === 'debit_transaction_no') {
                const moduleType = control.get('debit_module_type')?.value;
                const moduleId = control.get('debit_module_id')?.value;
                if (moduleId) {
                    if (moduleType === 'client_payment') {
                        params.paymentId = String(moduleId);
                    } else if (moduleType === 'client_credit_note') {
                        params.id = String(moduleId);
                    }
                    // JV debit lines use path-based routing, no query params needed
                }
            }

            // Skip journalVoucherId - JV lines use path-based routing with ID in route array
        }

        // Add mode if not already present
        if (params.mode === undefined && field?.redirect_mode !== undefined) {
            params.mode = field.redirect_mode;
        }

        return params;
    };

    constructor(
        private fb: FormBuilder,
        private clientSettlementService: ClientSettlementService,
        private clientListService: ClientListService,
        private toasterService: ToasterService
    ) { }

    async ngOnInit() {
        this.initializeForms();
        this.setupFormListeners();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private round(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }

    initializeForms() {
        // Primary form
        this.primaryForm = this.fb.group({
            client_id: [null],
            subsidiary_id: [null],
            mapping_date: [new Date()],
        });

        // Credit modules form - unified structure
        this.creditModulesForm = this.fb.group({
            credit_modules: this.fb.array([]),
        });

        // Debit modules form - unified structure
        this.debitModulesForm = this.fb.group({
            debit_modules: this.fb.array([]),
        });

        // Auto-apply mappings form (readonly)
        this.autoApplyMappingsForm = this.fb.group({
            mappings: this.fb.array([]),
        });
    }

    setupFormListeners() {
        // Listen to credit/debit form changes for auto-apply
        // Use debounceTime to ensure all checkbox changes are captured (especially for select all)
        this.creditModulesForm.valueChanges
            .pipe(
                debounceTime(300),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.calculateAutoApplyMappings();
            });

        this.debitModulesForm.valueChanges
            .pipe(
                debounceTime(300),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.calculateAutoApplyMappings();
            });
    }

    onSearchQuery(event: any) {
        if (event.fieldName === 'client_id') {
            this.handleClientSearch({ query: event.query });
        } else if (event.fieldName === 'subsidiary_id') {
            this.handleSubsidiarySearch({ query: event.query });
        }
    }

    onFieldChanged(event: any) {
        // Handle client selection
        if (event.key === 'client_id' && event.value) {
            this.onClientSelect(event.value);
        }
        // Handle subsidiary selection
        if (event.key === 'subsidiary_id' && event.value) {
            this.onSubsidiarySelect(event.value);
        }
    }

    onSelectionChanged(event: any, type: 'credit' | 'debit') {
        const formArray = type === 'credit' ? 
            this.creditModulesForm.get('credit_modules') as FormArray : 
            this.debitModulesForm.get('debit_modules') as FormArray;
            
        const paginatedData = type === 'credit' ? 
            this.creditModulesConfig.credit_modules.data : 
            this.debitModulesConfig.debit_modules.data;
            
        const allModules = type === 'credit' ? this.allCreditModules : this.allDebitModules;

        // Sync the current page's selection state back to the master list
        formArray.controls.forEach((control, index) => {
            const currentItem = paginatedData[index];
            if (currentItem) {
                // Find matching item in master list
                const masterItem = allModules.find(m => 
                    m.id === currentItem.id && m.module_type === currentItem.module_type
                );
                if (masterItem) {
                    masterItem._selected = !!control.get('_selected')?.value;
                }
            }
        });

        // Recalculate mappings
        this.calculateAutoApplyMappings();
    }

    async handleClientSearch(event: any) {
        const query = event.query || '';
        const clientControl = this.primaryForm.get('client_id');
        const currentClientId = clientControl?.value?.value || clientControl?.value?.id || null;

        try {
            // Use typeahead utility to search clients
            const result = await ClientTypeaheadUtils.searchClients(
                query,
                this.clientListService,
                currentClientId,
                this.allClients
            );

            // Update clients array with search results
            this.allClients = result.options;

            // Update client suggestions for autocomplete
            this.clientSuggestions = result.options.filter(
                (opt) => !currentClientId || String(opt.value) !== String(currentClientId)
            );

            // Update client field options
            const clientField = this.primaryFields.find(f => f.field_name === 'client_id');
            if (clientField) {
                clientField.options = this.clientSuggestions;
            }
        } catch (error) {
            console.error('Error searching clients:', error);
            this.toasterService.error('Error', 'Failed to search clients');
        }
    }

    /**
     * Load subsidiaries for a specific client
     */
    async loadClientSubsidiaries(clientId: string) {
        try {
            const response: any = await lastValueFrom(
                this.clientListService.getClientById(clientId, { include_subsidiary: 'true' })
            );
            // subsidiaries is a sibling to client in the response, not nested inside client
            const subsidiaries = response?.data?.subsidiaries || [];

            this.allSubsidiaries = subsidiaries.map((sub: any) => ({
                value: sub.id,
                label: sub.name || sub.subsidiary_name,
                ...sub
            }));

            // Update subsidiary field options
            const subsidiaryField = this.primaryFields.find(f => f.field_name === 'subsidiary_id');
            if (subsidiaryField) {
                subsidiaryField.options = this.allSubsidiaries;
            }

            // If only one subsidiary, auto-select it
            if (this.allSubsidiaries.length === 1) {
                this.primaryForm.get('subsidiary_id')?.setValue(this.allSubsidiaries[0]);
                this.selectedSubsidiary = this.allSubsidiaries[0];
                await this.loadModulesIfReady();
            }
        } catch (error) {
            console.error('Error loading client subsidiaries:', error);
            this.toasterService.error('Error', 'Failed to load subsidiaries for client');
        }
    }

    /**
     * Clear subsidiary selection when client changes
     */
    private clearSubsidiary() {
        this.selectedSubsidiary = null;
        this.allSubsidiaries = [];
        this.subsidiarySuggestions = [];
        this.primaryForm.get('subsidiary_id')?.setValue(null);
        
        // Clear subsidiary field options
        const subsidiaryField = this.primaryFields.find(f => f.field_name === 'subsidiary_id');
        if (subsidiaryField) {
            subsidiaryField.options = [];
        }
    }

    /**
     * Handle subsidiary search/filter
     */
    handleSubsidiarySearch(event: any) {
        const query = (event.query || '').toLowerCase();
        const subsidiaryControl = this.primaryForm.get('subsidiary_id');
        const currentSubsidiaryId = subsidiaryControl?.value?.value || subsidiaryControl?.value?.id || null;

        // Filter subsidiaries based on query
        this.subsidiarySuggestions = this.allSubsidiaries.filter(sub => {
            const matchesQuery = !query || sub.label.toLowerCase().includes(query);
            const notCurrentSelection = !currentSubsidiaryId || String(sub.value) !== String(currentSubsidiaryId);
            return matchesQuery && notCurrentSelection;
        });

        // Update subsidiary field options
        const subsidiaryField = this.primaryFields.find(f => f.field_name === 'subsidiary_id');
        if (subsidiaryField) {
            subsidiaryField.options = this.subsidiarySuggestions;
        }
    }

    async onClientSelect(client: any) {
        this.selectedClient = client;
        
        // Clear existing modules and subsidiary when client changes
        this.clearModules();
        this.clearSubsidiary();

        // Load subsidiaries for the selected client
        const clientId = client?.value || client?.id;
        if (clientId) {
            await this.loadClientSubsidiaries(String(clientId));
        }
    }

    async onSubsidiarySelect(subsidiary: any) {
        this.selectedSubsidiary = subsidiary;

        // Clear existing modules when subsidiary changes
        this.clearModules();

        // Load modules if both client and subsidiary are selected
        await this.loadModulesIfReady();
    }

    /**
     * Load modules only if both client and subsidiary are selected
     */
    private async loadModulesIfReady() {
        const clientId = this.selectedClient?.value || this.selectedClient?.id;
        const subsidiaryId = this.selectedSubsidiary?.value || this.selectedSubsidiary?.id;

        if (!clientId || !subsidiaryId) {
            return;
        }

        // Load credit and debit modules with subsidiary filter
        await Promise.all([
            this.loadCreditModules(clientId, subsidiaryId),
            this.loadDebitModules(clientId, subsidiaryId),
        ]);
    }

    /**
     * Clear all modules data
     */
    private clearModules() {
        this.allCreditModules = [];
        this.allDebitModules = [];
        this.creditModulesTotalCount = 0;
        this.debitModulesTotalCount = 0;
        this.creditModulesConfig.credit_modules.data = [];
        this.debitModulesConfig.debit_modules.data = [];
        this.autoApplyMappings = [];
        this.autoApplyMappingsConfig.mappings.data = [];
        
        // Reset forms
        const creditFormArray = this.creditModulesForm.get('credit_modules') as FormArray;
        const debitFormArray = this.debitModulesForm.get('debit_modules') as FormArray;
        const mappingsFormArray = this.autoApplyMappingsForm.get('mappings') as FormArray;
        
        if (creditFormArray) creditFormArray.clear();
        if (debitFormArray) debitFormArray.clear();
        if (mappingsFormArray) mappingsFormArray.clear();
    }

    async loadCreditModules(clientId: number, subsidiaryId: number) {
        this.loadingCreditModules = true;
        try {
            // Fetch all data with subsidiary filter
            const response: any = await lastValueFrom(
                this.clientSettlementService.getClientCreditModules(clientId, { subsidiary_id: subsidiaryId })
            );

            const unifiedCreditModules: any[] = [];

            // Process client invoices (amounts owed BY the client)
            const clientInvoices = response?.data?.client_invoices ?? [];
            clientInvoices.forEach((invoice: any) => {
                const totalPaidAmount = this.round(typeof invoice.total_paid_amount === 'string'
                    ? parseFloat(invoice.total_paid_amount)
                    : (invoice.total_paid_amount || 0));

                unifiedCreditModules.push({
                    id: invoice.id,
                    client_invoice_id: invoice.id,
                    module_type: 'client_invoice',
                    module_name: 'Invoice',
                    reference_no: invoice.invoice_reference_no,
                    redirect_route: '/clients/client-invoice',
                    redirect_mode: 'view',
                    redirect_query_params: {},
                    original_amount: this.round(invoice.final_amount),
                    unsettled_amount: this.round((invoice.final_amount || 0) - (invoice.tds_deducted || 0) - totalPaidAmount),
                    _selected: false,
                });
            });

            // Process JV credit lines (amounts owed BY the client via JV)
            const jvDebitLines = response?.data?.jv_debit_lines ?? [];
            jvDebitLines.forEach((jv: any) => {
                const totalMappedAmount = this.round(typeof jv.total_mapped_amount === 'string'
                    ? parseFloat(jv.total_mapped_amount)
                    : (jv.total_mapped_amount || 0));

                unifiedCreditModules.push({
                    id: jv.id,
                    journal_voucher_id: jv.journal_voucher_id,
                    module_type: 'jv_debit_line',
                    module_name: 'JV Debit',
                    reference_no: jv.transaction_no,
                    redirect_route: ['/finance/journal-vouchers', jv.journal_voucher_id, 'view'],
                    redirect_query_params: {},
                    original_amount: this.round(jv.debit),
                    unsettled_amount: this.round((jv.debit || 0) - totalMappedAmount),
                    _selected: false,
                });
            });

            // Note: Credit notes are processed in loadDebitModules since they reduce what client owes

            // Store all modules for local pagination
            this.allCreditModules = unifiedCreditModules;
            this.creditModulesTotalCount = unifiedCreditModules.length;

            // Apply local pagination
            this.applyCreditModulesPagination();

        } catch (error: any) {
            console.error('Error loading credit modules:', error);
            this.toasterService.error('Failed to load credit modules');
        } finally {
            this.loadingCreditModules = false;
        }
    }

    async loadDebitModules(clientId: number, subsidiaryId: number) {
        this.loadingDebitModules = true;
        try {
            // Fetch all data with subsidiary filter
            const response: any = await lastValueFrom(
                this.clientSettlementService.getClientDebitModules(clientId, { subsidiary_id: subsidiaryId })
            );

            const unifiedDebitModules: any[] = [];

            // Process client payments
            // API returns total_receivable_amount (not total_payment_amount)
            const clientPayments = response?.data?.client_payments ?? [];
            clientPayments.forEach((payment: any) => {
                const totalMappedAmount = this.round(typeof payment.total_mapped_amount === 'string'
                    ? parseFloat(payment.total_mapped_amount)
                    : (payment.total_mapped_amount || 0));

                // Use total_receivable_amount as the payment amount
                const paymentAmount = this.round(typeof payment.total_receivable_amount === 'string'
                    ? parseFloat(payment.total_receivable_amount)
                    : (payment.total_receivable_amount || 0));

                // Get total refunded amount
                const totalRefundedAmount = this.round(typeof payment.total_refunded_amount === 'string'
                    ? parseFloat(payment.total_refunded_amount)
                    : (payment.total_refunded_amount || 0));

                unifiedDebitModules.push({
                    id: payment.id,
                    client_payment_id: payment.id,
                    module_type: 'client_payment',
                    module_name: 'Payment',
                    reference_no: payment.transaction_no,
                    redirect_route: '/clients/client-payment',
                    redirect_mode: 'view',
                    redirect_query_params: {},
                    amount: paymentAmount,
                    // unmapped_amount = total_receivable_amount - total_mapped_amount - total_refunded_amount
                    unsettled_amount: this.round(paymentAmount - totalMappedAmount - totalRefundedAmount),
                    _selected: false,
                });
            });

            // Process client credit notes (credit notes reduce what client owes, so they are debit modules)
            const clientCreditNotes = response?.data?.client_credit_notes ?? [];
            clientCreditNotes.forEach((creditNote: any) => {
                const totalMappedAmount = this.round(typeof creditNote.total_mapped_amount === 'string'
                    ? parseFloat(creditNote.total_mapped_amount)
                    : (creditNote.total_mapped_amount || 0));

                // Credit note amount = amount field (already includes tax as final_amount)
                const creditNoteAmount = this.round(
                    typeof creditNote.amount === 'string' ? parseFloat(creditNote.amount) : (creditNote.amount || 0)
                );

                unifiedDebitModules.push({
                    id: creditNote.id,
                    credit_note_id: creditNote.id,
                    module_type: 'client_credit_note',
                    module_name: 'Credit Note',
                    reference_no: creditNote.credit_note_no,
                    redirect_route: '/clients/credit-note',
                    redirect_mode: 'view',
                    redirect_query_params: {},
                    amount: creditNoteAmount,
                    unsettled_amount: this.round(creditNoteAmount - totalMappedAmount),
                    _selected: false,
                });
            });

            // Process JV debit lines
            const jvCreditLines = response?.data?.jv_credit_lines ?? [];
            jvCreditLines.forEach((jvLine: any) => {
                const totalMappedAmount = this.round(typeof jvLine.total_mapped_amount === 'string'
                    ? parseFloat(jvLine.total_mapped_amount)
                    : (jvLine.total_mapped_amount || 0));

                unifiedDebitModules.push({
                    id: jvLine.id,
                    journal_voucher_id: jvLine.journal_voucher_id,
                    module_type: 'jv_credit_line',
                    module_name: 'JV Credit',
                    reference_no: jvLine.transaction_no,
                    redirect_route: ['/finance/journal-vouchers', jvLine.journal_voucher_id, 'view'],
                    redirect_mode: 'view',
                    redirect_query_params: {},
                    amount: this.round(jvLine.credit),
                    unsettled_amount: this.round((jvLine.credit || 0) - totalMappedAmount),
                    _selected: false,
                });
            });

            // Store all modules for local pagination
            this.allDebitModules = unifiedDebitModules;
            this.debitModulesTotalCount = unifiedDebitModules.length;

            // Apply local pagination
            this.applyDebitModulesPagination();

        } catch (error: any) {
            console.error('Error loading credit modules:', error);
            this.toasterService.error('Failed to load credit modules');
        } finally {
            this.loadingDebitModules = false;
        }
    }

    populateFormArray(arrayKey: string, data: any[], parentForm: FormGroup) {
        // Create a new FormArray
        const formArray = this.fb.array<any>([]);

        // Add a form group for each data row
        data.forEach((row: any) => {
            const rowGroup = this.fb.group({});

            // Add controls for each field in the row
            Object.keys(row).forEach(key => {
                rowGroup.addControl(key, this.fb.control(row[key]));
            });

            // Add _selected control for checkbox
            if (!rowGroup.get('_selected')) {
                rowGroup.addControl('_selected', this.fb.control(false));
            }

            formArray.push(rowGroup as any);
        });

        // Use setControl to replace the FormArray and trigger change detection
        parentForm.setControl(arrayKey, formArray);
    }

    calculateAutoApplyMappings() {
        const mappings: any[] = [];

        // Get selected credit rows and debit rows
        const selectedCreditRows = this.getSelectedRows('credit');
        const selectedDebitRows = this.getSelectedRows('debit');

        // Create copies with remaining amounts for greedy allocation
        const credits = selectedCreditRows.map(row => ({
            ...row,
            remaining: this.round(row.unsettled_amount || 0)
        }));
        const debits = selectedDebitRows.map(row => ({
            ...row,
            remaining: this.round(row.unsettled_amount || 0)
        }));

        let creditIdx = 0;
        let debitIdx = 0;

        while (creditIdx < credits.length && debitIdx < debits.length) {
            const credit = credits[creditIdx];
            const debit = debits[debitIdx];

            if (credit.remaining <= 0) {
                creditIdx++;
                continue;
            }
            if (debit.remaining <= 0) {
                debitIdx++;
                continue;
            }

            const amount = this.round(Math.min(credit.remaining, debit.remaining));

            if (amount > 0) {
                // Subtract the mapped amount first
                credit.remaining = this.round(credit.remaining - amount);
                debit.remaining = this.round(debit.remaining - amount);

                // Then add the mapping with the updated remaining amounts
                mappings.push({
                    credit_module_name: credit.module_name,
                    credit_module_type: credit.module_type,
                    credit_module_id: credit.id,
                    credit_transaction_no: credit.reference_no,
                    credit_transaction_no_redirect_route: credit.redirect_route,
                    credit_transaction_no_redirect_query_params: credit.redirect_query_params || {},
                    credit_unsettled: this.round(credit.unsettled_amount),
                    credit_remaining: this.round(credit.remaining),
                    debit_module_name: debit.module_name,
                    debit_module_type: debit.module_type,
                    debit_module_id: debit.id,
                    debit_transaction_no: debit.reference_no,
                    debit_transaction_no_redirect_route: debit.redirect_route,
                    debit_transaction_no_redirect_query_params: debit.redirect_query_params || {},
                    debit_unsettled: this.round(debit.unsettled_amount),
                    debit_remaining: this.round(debit.remaining),
                    mapped_amount: this.round(amount),
                });
            }

            if (credit.remaining <= 0) creditIdx++;
            if (debit.remaining <= 0) debitIdx++;
        }

        this.autoApplyMappings = mappings;
        
        // Populate ItemDynamicForm data
        this.autoApplyMappingsConfig.mappings.data = mappings;
        this.populateFormArray('mappings', mappings, this.autoApplyMappingsForm);
    }

    getSelectedRows(type: 'credit' | 'debit'): any[] {
        const allModules = type === 'credit' ? this.allCreditModules : this.allDebitModules;
        return allModules.filter(m => m._selected).map(m => ({ ...m }));
    }

    async onSubmit() {
        if (!this.selectedClient) {
            this.toasterService.error('Error', 'Please select a client');
            return;
        }

        if (this.autoApplyMappings.length === 0) {
            this.toasterService.error('Error', 'Please select at least one credit and one debit row');
            return;
        }

        this.isSubmitting = true;

        try {
            const mappingDate = this.primaryForm.get('mapping_date')?.value;
            const formattedDate = mappingDate ? moment(mappingDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

            const mappings: ClientSettlementMapping[] = this.autoApplyMappings.map((mapping) => ({
                client_id: this.selectedClient.value || this.selectedClient.id,
                credit_module_name: mapping.credit_module_type,
                credit_module_id: mapping.credit_module_id,
                debit_module_name: mapping.debit_module_type,
                debit_module_id: mapping.debit_module_id,
                mapped_amount: mapping.mapped_amount,
                mapping_date: formattedDate,
            }));

            const response = await lastValueFrom(
                this.clientSettlementService.createBulkSettlementMappings({ mappings })
            );

            if (response.status === 'success') {
                const successCount = response.data.successful.length;
                const failedCount = response.data.failed.length;

                if (failedCount === 0) {
                    this.toasterService.success('Success', `${successCount} settlement mappings created successfully`);
                    this.resetForm();
                } else {
                    this.toasterService.warn(
                        'Partial Success',
                        `${successCount} mappings created, ${failedCount} failed`
                    );
                    // Show failed mappings
                    response.data.failed.forEach((failed) => {
                        console.error(`Mapping ${failed.index} failed:`, failed.error);
                    });
                }
            }
        } catch (error: any) {
            console.error('Error creating settlement mappings:', error);
            this.toasterService.error('Error', error.error?.message || 'Failed to create settlement mappings');
        } finally {
            this.isSubmitting = false;
        }
    }

    resetForm() {
        this.primaryForm.reset({
            client_id: null,
            subsidiary_id: null,
            mapping_date: new Date(),
        });
        this.selectedClient = null;
        this.selectedSubsidiary = null;
        this.clearModules();
        this.clearSubsidiary();
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value);
    }

    formatDate(date: string | Date): string {
        return moment(date).format('DD/MM/YYYY');
    }

    getTotalMappedAmount(): number {
        return this.round(this.autoApplyMappings.reduce((sum, m) => sum + m.mapped_amount, 0));
    }

    getFieldKey(fieldName: string): string {
        return `field_${fieldName}`;
    }

    getRedirectUrl(mapping: any, type: 'credit' | 'debit'): string {
        const route = type === 'credit' ? mapping.credit_redirect_route : mapping.debit_redirect_route;
        const queryParams = type === 'credit' ? mapping.credit_redirect_query_params : mapping.debit_redirect_query_params;
        const moduleType = type === 'credit' ? mapping.credit_module_type : mapping.debit_module_type;
        const moduleId = type === 'credit' ? mapping.credit_module_id : mapping.debit_module_id;

        // Handle array routes (JV lines)
        if (Array.isArray(route)) {
            return route.join('/');
        }

        // Handle query param routes (invoices, payments, credit notes)
        const params = new URLSearchParams();
        
        // Add module-specific ID param
        if (moduleType === 'client_invoice') {
            params.set('invoiceId', moduleId);
        } else if (moduleType === 'client_payment') {
            params.set('billPaymentId', moduleId);
        } else if (moduleType === 'client_credit_note') {
            params.set('id', moduleId);
        }
        
        // Add mode param
        params.set('mode', 'view');
        
        // Add any additional query params
        Object.keys(queryParams || {}).forEach(key => {
            if (!params.has(key)) {
                params.set(key, queryParams[key]);
            }
        });

        return `${route}?${params.toString()}`;
    }

    /**
     * Helper function to create a delay using Promise
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Apply local pagination to debit modules
     */
    async applyDebitModulesPagination() {
        const startIndex = (this.debitModulesPage - 1) * this.debitModulesLimit;
        const endIndex = startIndex + this.debitModulesLimit;
        const paginatedData = this.allDebitModules.slice(startIndex, endIndex);

        // Create a new FormGroup with a new FormArray to force change detection
        this.debitModulesForm = this.fb.group({
            debit_modules: this.fb.array([])
        });

        // Update config data and populate the new FormArray
        this.debitModulesConfig.debit_modules.data = paginatedData;
        this.populateFormArray('debit_modules', paginatedData, this.debitModulesForm);
        
        // Wait for component to render before updating lastOpenedPage
        await this.delay(10);
        
        // Update last opened page to current page
        this.lastDebitModulesPage = this.debitModulesPage;
    }

    /**
     * Apply local pagination to credit modules
     */
    async applyCreditModulesPagination() {
        const startIndex = (this.creditModulesPage - 1) * this.creditModulesLimit;
        const endIndex = startIndex + this.creditModulesLimit;
        const paginatedData = this.allCreditModules.slice(startIndex, endIndex);

        // Create a new FormGroup with a new FormArray to force change detection
        this.creditModulesForm = this.fb.group({
            credit_modules: this.fb.array([])
        });

        // Update config data and populate the new FormArray
        this.creditModulesConfig.credit_modules.data = paginatedData;
        this.populateFormArray('credit_modules', paginatedData, this.creditModulesForm);
        
        // Wait for component to render before updating lastOpenedPage
        await this.delay(10);
        
        // Update last opened page to current page
        this.lastCreditModulesPage = this.creditModulesPage;
    }

    /**
     * Handle pagination change for debit modules (local pagination)
     */
    onDebitModulesPageChange(event: any) {
        this.debitModulesPage = event.page + 1; // PrimeNG paginator is 0-indexed
        this.debitModulesLimit = event.rows;
        
        // Apply local pagination
        this.applyDebitModulesPagination();
    }

    /**
     * Handle pagination change for credit modules (local pagination)
     */
    onCreditModulesPageChange(event: any) {
        this.creditModulesPage = event.page + 1; // PrimeNG paginator is 0-indexed
        this.creditModulesLimit = event.rows;
        
        // Apply local pagination
        this.applyCreditModulesPagination();
    }
}
